import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
  cloneData, cloneDataOrNull, cloneList, cloneListOrNull, isSameData, isSameList,
} from '../dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { PhotoReference } from './file-reference/photo-reference.js';
import { getSerialNumberScopeOfCountry, SerialNumberScope } from './serial-numbers/serial-number-scope.js';
import { LegacySerialNumberStrategy } from './serial-numbers/legacy-serial-number-strategy.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { getInvoiceVatFromValue, getInvoiceVatOfCountry, InvoiceVat } from './invoicing/invoice-vat.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';
import { Price } from './price.js';
import { ApplicationFeeProtocol } from './application-fee/application-fee-protocol.js';
import { LegacyPaymentSettings } from './legacy-payment-settings.js';
import { PaymentAccountRef } from './payment-account-ref.js';
import { PaymentType } from './payment-type.js';
import { TicketPrintFormat } from './ticket-print-format.js';
import { EventCategory } from './event-category.js';
import { getSerialNumberStrategyOfCountry, SerialNumberStrategy, SerialNumberStrategyUtils } from './serial-numbers/serial-number-strategy.js';
import { CustomInfo } from './custom-info.js';
import { QRCodeSeparator } from '../../utility/qr-code-utils.js';
import { JSONUtils } from '../../utility/json-utils.js';
import { getLanguageOfCountry, Language } from './language.js';
import { EventBookingStrategy } from './event-booking-protocol/event-booking-strategy.js';
import { PaymentMethod } from './payment-method.js';
import { Country } from './country.js';
import { EventProfileAssociations } from './event-profile-associations.js';

export class TicketSellingSettings extends DataModel {
  static readonly TYPE_ID: string = 'dcc8890db3aa45238df7c3c014998876';
  static readonly SCHEMA_VERSION: number = 31;

  private constructor(
    public readonly eventDefaultLanguage: Language | null,
    public readonly defaultFacebookPixelId: string,
    public readonly defaultTikTokPixelId: string,
    public readonly defaultGoogleTagManagerId: string,
    public readonly organizationLogo: PhotoReference | null,
    public readonly eventRules: string,
    public readonly organizationDetails: string,
    public readonly serialNumberScope: SerialNumberScope,
    public readonly serialNumberStrategy: SerialNumberStrategy,
    public readonly vat: InvoiceVat,
    public readonly feeProtocol: ApplicationFeeProtocol | null,
    public readonly canEmployeeEditPaymentSettings: boolean,
    public readonly paymentAccount: PaymentAccountRef | null,
    public readonly paymentType: PaymentType | null,
    public readonly activePaymentMethods: PaymentMethod[],

    // Dynamic pricing modifiers
    public readonly maxModifierPercentage: number | null,
    public readonly maxModifierPrice: Price | null,

    public readonly ticketPrintFormat: TicketPrintFormat,
    public readonly defaultEventClassification: EventCategory[],
    public readonly serialNumberLimits: string[] | null,
    public readonly allowLocalizedInputFields: boolean,
    public readonly organizationUniqueRegistrationCode: string,
    public readonly customInfo: CustomInfo | null,
    public readonly customImageInfo: PhotoReference | null,
    public readonly qrCodeSeparator: QRCodeSeparator,
    public readonly allowPredefinedProtocolTickets: boolean,
    public readonly allowOfflineOrderReceipts: boolean,
    public readonly eventBookingStrategy: EventBookingStrategy,
    public readonly eventProfileAssociations: EventProfileAssociations,
  ) {
    super();
  }

  get $typeId(): string {
    return TicketSellingSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(22, TicketSellingSettings.SCHEMA_VERSION);
  }

  static create(args: {
    eventDefaultLanguage: Language | null,
    defaultFacebookPixelId: string,
    defaultTikTokPixelId: string,
    defaultGoogleTagManagerId: string,
    organizationLogo: PhotoReference | null,
    eventRules: string,
    organizationDetails: string,
    serialNumberScope: SerialNumberScope,
    serialNumberStrategy: SerialNumberStrategy,
    vat: InvoiceVat,
    feeProtocol: ApplicationFeeProtocol | null,
    canEmployeeEditPaymentSettings: boolean,
    paymentAccount: PaymentAccountRef | null,
    paymentType: PaymentType | null,
    activePaymentMethods: PaymentMethod[],
    maxModifierPercentage: number | null,
    maxModifierPrice: Price | null,
    ticketPrintFormat: TicketPrintFormat,
    defaultEventClassification: EventCategory[],
    serialNumberLimits: string[] | null,
    allowLocalizedInputFields: boolean,
    organizationUniqueRegistrationCode: string,
    customInfo: CustomInfo | null,
    customImageInfo: PhotoReference | null,
    qrCodeSeparator: QRCodeSeparator,
    allowPredefinedProtocolTickets: boolean,
    allowOfflineOrderReceipts: boolean,
    eventBookingStrategy: EventBookingStrategy,
    eventProfileAssociations: EventProfileAssociations,
  }) {
    return new TicketSellingSettings(
        args.eventDefaultLanguage,
        args.defaultFacebookPixelId,
        args.defaultTikTokPixelId,
        args.defaultGoogleTagManagerId,
        args.organizationLogo,
        args.eventRules,
        args.organizationDetails,
        args.serialNumberScope,
        args.serialNumberStrategy,
        args.vat,
        args.feeProtocol,
        args.canEmployeeEditPaymentSettings,
        args.paymentAccount,
        args.paymentType,
        args.activePaymentMethods,
        args.maxModifierPercentage,
        args.maxModifierPrice,
        args.ticketPrintFormat,
        args.defaultEventClassification,
        args.serialNumberLimits,
        args.allowLocalizedInputFields,
        args.organizationUniqueRegistrationCode,
        args.customInfo,
        args.customImageInfo,
        args.qrCodeSeparator,
        args.allowPredefinedProtocolTickets,
        args.allowOfflineOrderReceipts,
        args.eventBookingStrategy,
        args.eventProfileAssociations,
    );
  }

  static fromCountry(country: Country): TicketSellingSettings {
    return new TicketSellingSettings(
        getLanguageOfCountry(country),
        '',
        '',
        '',
        null,
        '',
        '',
        getSerialNumberScopeOfCountry(country),
        getSerialNumberStrategyOfCountry(country),
        getInvoiceVatOfCountry(country),
        null,
        false,
        null,
        PaymentType.Online,
        Object.values(PaymentMethod),
        null,
        null,
        TicketPrintFormat.A4,
        [],
        null,
        false,
        '',
        null,
        null,
        QRCodeSeparator.Dot,
        false,
        false,
        EventBookingStrategy.Tickets,
        EventProfileAssociations.empty(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketSellingSettings {
    return this.create({
      eventDefaultLanguage: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'eventDefaultLanguage'), Language),
      defaultFacebookPixelId: MapUtils.getString(map, 'defaultFacebookPixelId'),
      defaultTikTokPixelId: MapUtils.getString(map, 'defaultTikTokPixelId'),
      defaultGoogleTagManagerId: MapUtils.getString(map, 'defaultGoogleTagManagerId'),
      organizationLogo: context.fromDataOrNull<PhotoReference>(MapUtils.getRecordOrNull(map, 'organizationLogo')),
      eventRules: MapUtils.getString(map, 'eventRules'),
      organizationDetails: MapUtils.getString(map, 'organizationDetails'),
      serialNumberScope: toEnumFromString(MapUtils.getString(map, 'serialNumberScope'), SerialNumberScope),
      serialNumberStrategy: toEnumFromString(MapUtils.getString(map, 'serialNumberStrategy'), SerialNumberStrategy),
      vat: toEnumFromString(MapUtils.getString(map, 'vat'), InvoiceVat),
      feeProtocol: context.fromDataOrNull<ApplicationFeeProtocol>(MapUtils.getRecordOrNull(map, 'feeProtocol')),
      canEmployeeEditPaymentSettings: MapUtils.getBoolean(map, 'canEmployeeEditPaymentSettings'),
      paymentAccount: context.fromDataOrNull<PaymentAccountRef>(MapUtils.getRecordOrNull(map, 'paymentAccount')),
      paymentType: toEnumFromString(MapUtils.getString(map, 'paymentType'), PaymentType),
      activePaymentMethods: MapUtils.getList(map, 'activePaymentMethods', (x) => toEnumFromString(JSONUtils.decodeString(x), PaymentMethod)),
      maxModifierPercentage: MapUtils.getNumberOrNull(map, 'maxModifierPercentage'),
      maxModifierPrice: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'maxModifierPrice')),
      ticketPrintFormat: toEnumFromString(MapUtils.getString(map, 'ticketPrintFormat'), TicketPrintFormat),
      defaultEventClassification: MapUtils.getList<EventCategory>(
          map,
          'defaultEventClassification',
          (x) => toEnumFromString(JSONUtils.decodeString(x), EventCategory)),
      serialNumberLimits: MapUtils.getListOrNull<string>(map, 'serialNumberLimits', (value) => JSONUtils.decodeString(value)),
      allowLocalizedInputFields: MapUtils.getBoolean(map, 'allowLocalizedInputFields'),
      organizationUniqueRegistrationCode: MapUtils.getString(map, 'organizationUniqueRegistrationCode'),
      customInfo: context.fromDataOrNull<CustomInfo>(MapUtils.getRecordOrNull(map, 'customInfo')),
      customImageInfo: context.fromDataOrNull<PhotoReference>(MapUtils.getRecordOrNull(map, 'customImageInfo')),
      qrCodeSeparator: toEnumFromString(MapUtils.getString(map, 'qrCodeSeparator'), QRCodeSeparator),
      allowPredefinedProtocolTickets: MapUtils.getBoolean(map, 'allowPredefinedProtocolTickets'),
      allowOfflineOrderReceipts: MapUtils.getBoolean(map, 'allowOfflineOrderReceipts'),
      eventBookingStrategy: toEnumFromString(MapUtils.getString(map, 'eventBookingStrategy'), EventBookingStrategy),
      eventProfileAssociations: context.fromData<EventProfileAssociations>(MapUtils.getRecord(map, 'eventProfileAssociations')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'eventDefaultLanguage': fromEnumAsStringOrNull(this.eventDefaultLanguage),
      'defaultFacebookPixelId': this.defaultFacebookPixelId,
      'defaultTikTokPixelId': this.defaultTikTokPixelId,
      'defaultGoogleTagManagerId': this.defaultGoogleTagManagerId,
      'organizationLogo': context.toDataOrNull(this.organizationLogo),
      'eventRules': this.eventRules,
      'organizationDetails': this.organizationDetails,
      'serialNumberScope': fromEnumAsString(this.serialNumberScope),
      'serialNumberStrategy': fromEnumAsString(this.serialNumberStrategy),
      'vat': fromEnumAsString(this.vat),
      'feeProtocol': context.toDataOrNull(this.feeProtocol),
      'canEmployeeEditPaymentSettings': this.canEmployeeEditPaymentSettings,
      'paymentAccount': context.toDataOrNull(this.paymentAccount),
      'paymentType': fromEnumAsString(this.paymentType),
      'activePaymentMethods': this.activePaymentMethods.map((x) => fromEnumAsString(x)),
      'maxModifierPercentage': this.maxModifierPercentage,
      'maxModifierPrice': this.maxModifierPrice?.encode() ?? null,
      'ticketPrintFormat': fromEnumAsString(this.ticketPrintFormat),
      'defaultEventClassification': this.defaultEventClassification.map((x) => fromEnumAsString(x)),
      'serialNumberLimits': this.serialNumberLimits,
      'allowLocalizedInputFields': this.allowLocalizedInputFields,
      'organizationUniqueRegistrationCode': this.organizationUniqueRegistrationCode,
      'customInfo': context.toDataOrNull(this.customInfo),
      'customImageInfo': context.toDataOrNull(this.customImageInfo),
      'qrCodeSeparator': fromEnumAsString(this.qrCodeSeparator),
      'allowPredefinedProtocolTickets': this.allowPredefinedProtocolTickets,
      'allowOfflineOrderReceipts': this.allowOfflineOrderReceipts,
      'eventBookingStrategy': fromEnumAsString(this.eventBookingStrategy),
      'eventProfileAssociations': context.toData(this.eventProfileAssociations),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.eventDefaultLanguage === this.eventDefaultLanguage &&
      other.defaultFacebookPixelId === this.defaultFacebookPixelId &&
      other.defaultTikTokPixelId === this.defaultTikTokPixelId &&
      other.defaultGoogleTagManagerId === this.defaultGoogleTagManagerId &&
      isSameData(other.organizationLogo, this.organizationLogo) &&
      other.eventRules === this.eventRules &&
      other.organizationDetails === this.organizationDetails &&
      other.serialNumberScope === this.serialNumberScope &&
      other.serialNumberStrategy === this.serialNumberStrategy &&
      other.vat === this.vat &&
      isSameData(other.feeProtocol, this.feeProtocol) &&
      other.canEmployeeEditPaymentSettings === this.canEmployeeEditPaymentSettings &&
      isSameData(other.paymentAccount, this.paymentAccount) &&
      other.paymentType === this.paymentType &&
      isSameList(other.activePaymentMethods, this.activePaymentMethods) &&
      other.maxModifierPercentage === this.maxModifierPercentage &&
      isSameData(other.maxModifierPrice, this.maxModifierPrice) &&
      other.ticketPrintFormat === this.ticketPrintFormat &&
      isSameList(other.defaultEventClassification, this.defaultEventClassification) &&
      isSameList(other.serialNumberLimits, this.serialNumberLimits) &&
      other.allowLocalizedInputFields === this.allowLocalizedInputFields &&
      other.organizationUniqueRegistrationCode === this.organizationUniqueRegistrationCode &&
      isSameData(other.customInfo, this.customInfo) &&
      isSameData(other.customImageInfo, this.customImageInfo) &&
      other.qrCodeSeparator === this.qrCodeSeparator &&
      other.allowPredefinedProtocolTickets === this.allowPredefinedProtocolTickets &&
      other.allowOfflineOrderReceipts === this.allowOfflineOrderReceipts &&
      other.eventBookingStrategy === this.eventBookingStrategy &&
      isSameData(other.eventProfileAssociations, this.eventProfileAssociations);
  }

  copyWith({
    eventDefaultLanguage = this.eventDefaultLanguage,
    defaultFacebookPixelId = this.defaultFacebookPixelId,
    defaultTikTokPixelId = this.defaultTikTokPixelId,
    defaultGoogleTagManagerId = this.defaultGoogleTagManagerId,
    organizationLogo = cloneDataOrNull(this.organizationLogo),
    eventRules = this.eventRules,
    organizationDetails = this.organizationDetails,
    serialNumberScope = this.serialNumberScope,
    serialNumberStrategy = this.serialNumberStrategy,
    vat = this.vat,
    feeProtocol = cloneDataOrNull(this.feeProtocol),
    canEmployeeEditPaymentSettings = this.canEmployeeEditPaymentSettings,
    paymentAccount = cloneDataOrNull(this.paymentAccount),
    paymentType = this.paymentType,
    activePaymentMethods = cloneList(this.activePaymentMethods),
    maxModifierPercentage = this.maxModifierPercentage,
    maxModifierPrice = cloneDataOrNull(this.maxModifierPrice),
    ticketPrintFormat = this.ticketPrintFormat,
    defaultEventClassification = cloneList(this.defaultEventClassification),
    serialNumberLimits = cloneListOrNull(this.serialNumberLimits),
    allowLocalizedInputFields = this.allowLocalizedInputFields,
    organizationUniqueRegistrationCode = this.organizationUniqueRegistrationCode,
    customInfo = cloneDataOrNull(this.customInfo),
    customImageInfo = cloneDataOrNull(this.customImageInfo),
    qrCodeSeparator = this.qrCodeSeparator,
    allowPredefinedProtocolTickets = this.allowPredefinedProtocolTickets,
    allowOfflineOrderReceipts = this.allowOfflineOrderReceipts,
    eventBookingStrategy = this.eventBookingStrategy,
    eventProfileAssociations = cloneData(this.eventProfileAssociations),
  }: {
    eventDefaultLanguage?: Language | null,
    defaultFacebookPixelId?: string,
    defaultTikTokPixelId?: string,
    defaultGoogleTagManagerId?: string,
    organizationLogo?: PhotoReference | null,
    eventRules?: string,
    organizationDetails?: string,
    serialNumberScope?: SerialNumberScope,
    serialNumberStrategy?: SerialNumberStrategy,
    vat?: InvoiceVat,
    feeProtocol?: ApplicationFeeProtocol | null,
    canEmployeeEditPaymentSettings?: boolean,
    paymentAccount?: PaymentAccountRef | null,
    paymentType?: PaymentType | null,
    activePaymentMethods?: PaymentMethod[],
    maxModifierPercentage?: number | null,
    maxModifierPrice?: Price | null,
    ticketPrintFormat?: TicketPrintFormat,
    defaultEventClassification?: EventCategory[],
    serialNumberLimits?: string[] | null,
    allowLocalizedInputFields?: boolean,
    organizationUniqueRegistrationCode?: string,
    customInfo?: CustomInfo | null,
    customImageInfo?: PhotoReference | null,
    qrCodeSeparator?: QRCodeSeparator,
    allowPredefinedProtocolTickets?: boolean,
    allowOfflineOrderReceipts?: boolean,
    eventBookingStrategy?: EventBookingStrategy,
    eventProfileAssociations?: EventProfileAssociations,
  } = {}): this {
    return TicketSellingSettings.create({
      eventDefaultLanguage,
      defaultFacebookPixelId,
      defaultTikTokPixelId,
      defaultGoogleTagManagerId,
      organizationLogo,
      eventRules,
      organizationDetails,
      serialNumberScope,
      serialNumberStrategy,
      vat,
      feeProtocol,
      canEmployeeEditPaymentSettings,
      paymentAccount,
      paymentType,
      activePaymentMethods,
      maxModifierPercentage,
      maxModifierPrice,
      ticketPrintFormat,
      defaultEventClassification,
      serialNumberLimits,
      allowLocalizedInputFields,
      organizationUniqueRegistrationCode,
      customInfo,
      customImageInfo,
      qrCodeSeparator,
      allowPredefinedProtocolTickets,
      allowOfflineOrderReceipts,
      eventBookingStrategy,
      eventProfileAssociations,
    }) as this;
  }
}

export class TicketSellingSettingsBuilder extends DataModelBuilder<TicketSellingSettings> {
  build(context: SerializationContext, map: SerializedData): TicketSellingSettings {
    return TicketSellingSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    this.migrateEnumList(map, 'defaultEventClassification', EventCategory);
    this.migrateEnum(map, 'ticketPrintFormat', TicketPrintFormat, TicketPrintFormat.Boca6x11);
    this.migrateEnum(map, 'qrCodeSeparator', QRCodeSeparator, QRCodeSeparator.Dot);

    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TicketSellingSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(TicketSellingSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the `organizationName` and `registrationCode` parameters of type `string` were introduced.
    if(version < 2) {
      map['organizationName'] ??= '';
      map['registrationCode'] ??= '';
      version = 2;
    }

    // In this version the `organizationLogo` parameter of type `PhotoReference | null` was introduced.
    if(version < 3) {
      version = 3;
    }

    // In this version the following changes were made
    //  - `defaultPixelId` parameter was renamed to `defaultFacebookPixelId`
    //  - `defaultTikTokPixelId` parameter of type `string` was introduced
    if(version < 4) {
      map['defaultFacebookPixelId'] ??= MapUtils.get<string>(map, 'defaultPixelId');
      map['defaultTikTokPixelId'] ??= '';
      version = 4;
    }

    // In this version the `importantInfo` and `organizationDetails` parameters
    // of type `string` were introduced.
    if(version < 5) {
      map['importantInfo'] ??= '';
      map['organizationDetails'] ??= '';
      version = 5;
    }

    // In this version the `feeRanges` parameter was removed.
    if(version < 6) {
      version = 6;
    }

    // In this version the following changes were made:
    //  - `serialNumberScope` parameter of type `SerialNumberScope` was
    //    introduced
    //  - `serialNumberStrategy` parameter of type `SerialNumberStrategy` was
    //    introduced
    if(version < 7) {
      map['serialNumberScope'] ??= fromEnumAsString(SerialNumberScope.Event);
      map['serialNumberStrategy'] ??= fromEnumAsString(LegacySerialNumberStrategy.AssignByTicketType);
      version = 7;
    }

    // In this version `vatPercent` parameter of type `double` was introduced.
    // Until this moment we used constant `27%` VAT.
    if(version < 8) {
      map['vatPercent'] ??= 27.0;
      version = 8;
    }

    // In this version `vatPercent` parameter of type `double` was replaced
    // with parameter `vat` of type `InvoiceVat`.
    if(version < 9) {
      if(isNullOrUndefined(map['vat'])) {
        const vatPercent = MapUtils.getNumber(map, 'vatPercent');
        const vat = getInvoiceVatFromValue(Math.trunc(vatPercent));
        map['vat'] = fromEnumAsString(vat) ?? InvoiceVat.Vat_27;
      }

      version = 9;
    }

    // In this version the following changes were made:
    //  - `maxModifierPercentage` parameter of type `number | null` was
    //    introduced
    //  - `maxModifierPrice` parameter of type `Price | null` was
    //    introduced
    if(version < 10) {
      version = 10;
    }

    // In this version `feeProtocol` parameter of type `ApplicationFeeProtocol | null` was introduced.
    if(version < 11) {
      version = 11;
    }

    // In this version `paymentSettings` parameter of type `PaymentSettings | null` was introduced.
    if(version < 12) {
      version = 12;
    }

    // In this version `canEmployeeEditPaymentSettings` parameter of type `boolean` was introduced.
    if(version < 13) {
      map['canEmployeeEditPaymentSettings'] ??= false;
      version = 13;
    }

    if(version < 14) {
      const paymentSettings = context.fromDataOrNull<LegacyPaymentSettings>(MapUtils.getRecordOrNull(map, 'paymentSettings'));
      if(paymentSettings != null) {
        map['paymentAccount'] = context.toDataOrNull(paymentSettings.paymentAccount ?? null);
        map['paymentType'] = fromEnumAsString(paymentSettings.paymentMethods.length > 0 ? paymentSettings.paymentMethods[0] : PaymentType.OnDelivery);
      }
      else {
        map['paymentAccount'] = null;
        map['paymentType'] = fromEnumAsString(PaymentType.OnDelivery);
      }
      version = 14;
    }

    // In this version `importantInfo` was renamed to `eventRules`.
    if(version < 15) {
      map['eventRules'] ??= map['importantInfo'];
      version = 15;
    }

    // In this version `ticketPrintFormat` parameter of type
    // `TicketPrintFormat` was introduced.
    if(version < 16) {
      map['ticketPrintFormat'] ??= fromEnumAsString(TicketPrintFormat.Boca6x11);
      version = 16;
    }

    if(version < 17) {
      map['defaultEventClassification'] ??= [];
      version = 17;
    }

    // Version 18:
    // - add `serialNumberLimits` parameter of type string[] | null`
    if(version < 18) {
      version = 18;
    }

    // Version 19:
    // - add `allowLocalizedInputFields` parameter of type boolean`
    if(version < 19) {
      map['allowLocalizedInputFields'] ??= false;
      version = 19;
    }

    // In this version the following changes were made
    //  - `defaultTikTokPixelId` parameter of type `string` was introduced
    if(version < 20) {
      map['defaultGoogleTagManagerId'] ??= '';
      version = 20;
    }

    // In this version the following changes were made
    //  - `organizationUniqueRegistrationCode` parameter of type `String` was introduced
    if(version < 21) {
      map['organizationUniqueRegistrationCode'] ??= '';
      version = 21;
    }

    /**
     * Version 22:
     * - change `serialNumberStrategy` parameter from type {@link LegacySerialNumberStrategy} to {@link SerialNumberStrategy}.
     */
    if(version < 22) {
      const legacySerialNumberStrategy = toEnumFromString(MapUtils.getString(map, 'serialNumberStrategy'), LegacySerialNumberStrategy);
      const serialNumberStrategy = SerialNumberStrategyUtils.fromLegacy(legacySerialNumberStrategy);

      MapUtils.set(map, 'serialNumberStrategy', fromEnumAsString(serialNumberStrategy));

      version = 22;
    }

    /**
     * Version 23:
     *  - `customInfo` parameter of type {@link CustomInfo | null} was introduced
     */
    if(version < 23) {
      version = 23;
    }

    /**
     * Version 24:
     *  - `customImageInfo` parameter of type {@link PhotoReference | null} was introduced
     */
    if(version < 24) {
      version = 24;
    }

    /**
     * Version 25:
     *  - `qrCodeSeparator` parameter of type {@link QRCodeSeparator}  was introduced
     */
    if(version < 25) {
      MapUtils.setIfNullOrUndefined(map, 'qrCodeSeparator', fromEnumAsString(QRCodeSeparator.Dot));
      version = 25;
    }

    /**
     * Version 26:
     * - add `allowPredefinedProtocolTickets` field of type {@link boolean} was introduced
     */
    if(version < 26) {
      MapUtils.setIfNullOrUndefined(map, 'allowPredefinedProtocolTickets', false);
      version = 26;
    }

    /**
     * Version 27:
     * - add `allowOfflineOrderReceipts` field of type {@link boolean} was introduced
     */
    if(version < 27) {
      MapUtils.setIfNullOrUndefined(map, 'allowOfflineOrderReceipts', false);
      version = 27;
    }

    /**
     * Version 28:
     * - add `eventBookingStrategy` field of type {@link EventBookingStrategy} was introduced
     */
    if(version < 28) {
      MapUtils.setIfNullOrUndefined(map, 'eventBookingStrategy', fromEnumAsString(EventBookingStrategy.Tickets));
      version = 28;
    }

    /**
     * Version 29:
     * - add `eventDefaultLanguage` field of type {@link Language} was introduced
    **/
    if(version < 29) {
      version = 29;
    }

    /**
     * Version 30:
     * - add `activePaymentMethods` field of type {@link PaymentMethod[]} was introduced
     **/
    if(version < 30) {
      map['activePaymentMethods'] ??= Object.values(PaymentMethod).map(fromEnumAsString);
      version = 30;
    }

    /**
     * Version 31:
     * - add `eventProfileAssociations` field of type {@link EventProfileAssociations} was introduced
     **/
    if(version < 31) {
      map['eventProfileAssociations'] ??= context.toData(EventProfileAssociations.empty());
      version = 31;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
